import { ref, shallowRef, computed } from "vue";
import { MabiDB } from "@/mabidb";
import { ActorManager } from "@/eventActor";
import { DamageCollectorManager } from "@/actionCollector";
import { skills } from "./data/skill";
import { races } from "./data/race";
import { conditions } from "./data/condition";
import { EFFECT_STATE_BUFFS } from "@/effectState";

export const loadingCount = ref(0);
export const isLoading = computed(() => loadingCount.value > 0);

// support: ["kr",  "cn", "jp", "tw", "us"]
const defaultRegion = getAutoRegion(); //default:tw

export const region = ref(defaultRegion);

const LANG_KEY = 'mabidil_lang';
export const lang = ref<string>(localStorage.getItem(LANG_KEY) ?? defaultRegion);
export function setLang(newLang: string): void {
    lang.value = newLang;
    localStorage.setItem(LANG_KEY, newLang);
}

export const regionList = ref([defaultRegion]);

export const db = computed(() => {
    const instance = new MabiDB(region.value, lang.value);

    // // fire and forget
    // instance.open().catch(e => console.error(e));

    return instance;
});

// ── 玩家改名（全域） ──────────────────────────────────────────
export const idMappings = ref<Record<string, string>>({});
export const getDisplayName = (name: string): string => idMappings.value[name] || name;

// ── 技能別稱（全域） ──────────────────────────────────────────
const SKILL_ALIASES_KEY = "mabidil_skillAliases";

function loadSkillAliases(): Record<number, string> {
    try {
        const raw = localStorage.getItem(SKILL_ALIASES_KEY);
        if (raw) return JSON.parse(raw) as Record<number, string>;
    } catch { /* ignore */ }
    return {};
}

export const skillAliases = ref<Record<number, string>>(loadSkillAliases());

export function setSkillAlias(skillId: number, alias: string): void {
    const next = { ...skillAliases.value };
    if (alias.trim()) {
        next[skillId] = alias.trim();
    } else {
        delete next[skillId];
    }
    skillAliases.value = next;
    localStorage.setItem(SKILL_ALIASES_KEY, JSON.stringify(next));
}

export function removeSkillAlias(skillId: number): void {
    const next = { ...skillAliases.value };
    delete next[skillId];
    skillAliases.value = next;
    localStorage.setItem(SKILL_ALIASES_KEY, JSON.stringify(next));
}

/** 取得技能顯示名稱：別稱 > skillNameMap > #id */
export function getSkillName(skillId: number, map: Record<number, string>): string {
    return skillAliases.value[skillId] || map[skillId] || `#${skillId}`;
}

export const raceNameMap      = ref<Record<number, string>>({ ...races });
export const skillNameMap     = ref<Record<number, string>>({ ...skills });

// ── CC 別稱（全域） ────────────────────────────────────────────
const COND_ALIASES_KEY = "mabidil_condAliases";

function loadCondAliases(): Record<number, string> {
    try {
        const raw = localStorage.getItem(COND_ALIASES_KEY);
        if (raw) return JSON.parse(raw) as Record<number, string>;
    } catch { /* ignore */ }
    return {};
}

export const condAliases = ref<Record<number, string>>(loadCondAliases());

export function setCondAlias(ccId: number, alias: string): void {
    const next = { ...condAliases.value };
    if (alias.trim()) {
        next[ccId] = alias.trim();
    } else {
        delete next[ccId];
    }
    condAliases.value = next;
    localStorage.setItem(COND_ALIASES_KEY, JSON.stringify(next));
}

export function removeCondAlias(ccId: number): void {
    const next = { ...condAliases.value };
    delete next[ccId];
    condAliases.value = next;
    localStorage.setItem(COND_ALIASES_KEY, JSON.stringify(next));
}

export const condNameMap      = ref<Record<number, string>>({
    ...conditions,
    // 合成 buff（effect-state）名稱：優先用對應技能名，否則用 def.name。
    // reloadNameMaps() 會在 skillNameMap 更新後重新設定這些鍵。
    ...Object.fromEntries(
        EFFECT_STATE_BUFFS.map((d) => [
            d.ccId,
            skills[String(d.iconSkillId) as keyof typeof skills] ?? d.name,
        ]),
    ),
});
/** condNameMap (DB 原始名) + condAliases 合併後的對外用 map；由 main.ts 以此取代 condNameMap provide */
export const effectiveCondNameMap = computed<Record<number, string>>(() => ({
    ...condNameMap.value,
    ...condAliases.value,
}));

export const itemNameMap      = ref<Record<number, string>>({});
export const titleNameMap     = ref<Record<number, string>>({});
export const miniatureNameMap = ref<Record<number, string>>({});
export const optionSetNameMap = ref<Record<number, string>>({});
export const appEvent = ref(new EventTarget());

export const dcManager = shallowRef(new DamageCollectorManager());
export const actorManager = shallowRef(
    new ActorManager(dcManager.value as DamageCollectorManager),
);

/** 最後一次從檔案或伺服器載入的原始 ndjson 字串，供 Export 功能使用 */
export const rawNdjson = ref<string>("");

export const timeRangeMin = ref<number | null>(null);
export const timeRangeMax = ref<number | null>(null);
export const hasTimeRange = computed(
    () => timeRangeMin.value !== null && timeRangeMax.value !== null,
);

export function setTimeRange(min: number, max: number) {
    timeRangeMin.value = min;
    timeRangeMax.value = max;
}

export function clearTimeRange() {
    timeRangeMin.value = null;
    timeRangeMax.value = null;
}

// config
const CONFIG_STORAGE_KEY = "config";

export interface AppConfig {
    hiddenCCIds: number[];
    hiddenRaceIds: number[];
    showDate: boolean;
}

const defaultConfig: AppConfig = {
    hiddenCCIds: [],
    hiddenRaceIds: [],
    showDate: false,
};

function loadConfig(): AppConfig {
    try {
        const raw = localStorage.getItem(CONFIG_STORAGE_KEY);
        if (raw) return { ...defaultConfig, ...JSON.parse(raw) };
    } catch {
        /* ignore */
    }
    return { ...defaultConfig };
}

function saveConfig() {
    const data: AppConfig = {
        hiddenCCIds: [...hiddenCCIds.value],
        hiddenRaceIds: [...hiddenRaceIds.value],
        showDate: showDate.value,
    };
    localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(data));
}

const _config = loadConfig();

export const hiddenCCIds = ref<Set<number>>(new Set(_config.hiddenCCIds));

export function addHiddenCC(ccId: number) {
    hiddenCCIds.value = new Set(hiddenCCIds.value).add(ccId);
    saveConfig();
}

export function removeHiddenCC(ccId: number) {
    const next = new Set(hiddenCCIds.value);
    next.delete(ccId);
    hiddenCCIds.value = next;
    saveConfig();
}

export const hiddenRaceIds = ref<Set<number>>(new Set(_config.hiddenRaceIds));

export function addHiddenRace(raceId: number) {
    hiddenRaceIds.value = new Set(hiddenRaceIds.value).add(raceId);
    saveConfig();
}

export function removeHiddenRace(raceId: number) {
    const next = new Set(hiddenRaceIds.value);
    next.delete(raceId);
    hiddenRaceIds.value = next;
    saveConfig();
}

export const showDate = ref<boolean>(_config.showDate ?? false);

export function setShowDate(v: boolean) {
    showDate.value = v;
    saveConfig();
}

function getAutoRegion() {
    try {
        // 1. get IANA Timezone ID (ex "Asia/Taipei", "Asia/Seoul")
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const lang = navigator.language.toLowerCase();

        if (timeZone.includes("Taipei")) return "tw";
        if (
            timeZone.includes("Shanghai") ||
            timeZone.includes("Chongqing") ||
            timeZone.includes("Urumqi")
        )
            return "cn";
        // 處理香港/澳門 (通常遊戲分區會歸類在 TW 或 CN，看你需求，這裡範例歸在 TW)
        if (timeZone.includes("Hong_Kong")) return "tw";

        // 處理 KR vs JP (UTC+9 衝突)
        if (timeZone.includes("Seoul")) return "kr";
        if (timeZone.includes("Tokyo")) return "jp";

        // 處理 US (美洲時區眾多，用前綴判斷)
        if (timeZone.startsWith("America/")) return "us";

        // --- 模糊比對 (Fallback) ---
        // 如果上面的具體城市都沒抓到 (例如使用者時區設為 Generic Asia/UTC+8)
        // 則改用「語系」來猜

        if (lang.includes("zh-tw") || lang.includes("zh-hk")) return "tw";
        if (lang.includes("zh")) return "cn";
        if (lang.includes("ko")) return "kr";
        if (lang.includes("ja")) return "jp";

        // 預設回傳 (例如以上皆非，預設使用繁體中文台服)
        return "tw";
    } catch (e) {
        console.error("自動偵測失敗:", e);
        return "tw"; // 發生錯誤時的保底
    }
}
